"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { readKioskPermissionRequestMode } from "../lib/kiosk-settings";

// Debug mode - set to true to enable camera provider logging
const DEBUG_CAMERA_PROVIDER = false;

// Camera label preferences for fallback selection (in order of priority)
// iPad cameras typically have labels like "Front Camera", "Back Camera", "Back Ultra Wide Camera"
const PREFERRED_FRONT_CAMERA_LABELS = [
  "Front Camera", // Prefer front camera
  "FaceTime", // FaceTime HD camera (Mac)
];

const PREFERRED_BACK_CAMERA_LABELS = [
  "Back Camera", // Prefer back camera (not ultrawide)
];

// Labels to avoid (ultrawide tends to have poor quality for document scanning)
const AVOID_CAMERA_LABELS = ["ultra wide", "ultrawide", "wide angle"];

/**
 * Find the best camera device based on label preferences
 * Returns the device ID of the preferred camera, or null if no preference matched
 */
async function findPreferredCamera(
  facingMode: "user" | "environment" = "user",
): Promise<string | null> {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const videoDevices = devices.filter((d) => d.kind === "videoinput");

    if (videoDevices.length === 0) return null;

    const preferredLabels =
      facingMode === "environment"
        ? PREFERRED_BACK_CAMERA_LABELS
        : PREFERRED_FRONT_CAMERA_LABELS;

    // First, try to find a preferred camera by label
    for (const preferred of preferredLabels) {
      const match = videoDevices.find(
        (d) =>
          d.label.toLowerCase().includes(preferred.toLowerCase()) &&
          !AVOID_CAMERA_LABELS.some((avoid) =>
            d.label.toLowerCase().includes(avoid.toLowerCase()),
          ),
      );
      if (match) {
        if (DEBUG_CAMERA_PROVIDER) {
          console.log(
            `[KioskCameraProvider] Found preferred camera: ${match.label}`,
          );
        }
        return match.deviceId;
      }
    }

    // No known label match — return null so caller falls back to facingMode
    return null;
  } catch (error) {
    console.error("[KioskCameraProvider] Failed to enumerate devices:", error);
    return null;
  }
}

/**
 * Find the best back-facing camera (for QR mode document scanning)
 * Returns the device ID of the preferred rear camera, or null if no preference matched
 */
async function findPreferredBackCamera(): Promise<string | null> {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const videoDevices = devices.filter((d) => d.kind === "videoinput");

    if (videoDevices.length === 0) return null;

    for (const preferred of PREFERRED_BACK_CAMERA_LABELS) {
      const match = videoDevices.find(
        (d) =>
          d.label.toLowerCase().includes(preferred.toLowerCase()) &&
          !AVOID_CAMERA_LABELS.some((avoid) =>
            d.label.toLowerCase().includes(avoid.toLowerCase()),
          ),
      );
      if (match) {
        if (DEBUG_CAMERA_PROVIDER) {
          console.log(
            `[KioskCameraProvider] Found preferred back camera: ${match.label}`,
          );
        }
        return match.deviceId;
      }
    }

    // Last resort: use facingMode "environment" via constraints, not deviceId
    return null;
  } catch (error) {
    console.error("[KioskCameraProvider] Failed to enumerate devices:", error);
    return null;
  }
}

/**
 * Check if a device ID is still valid (exists in available devices)
 */
async function isDeviceIdValid(deviceId: string): Promise<boolean> {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.some(
      (d) => d.kind === "videoinput" && d.deviceId === deviceId,
    );
  } catch {
    return false;
  }
}

// Configure when to request camera permission - can be controlled from
// DevTool Settings. Defaults sourced from lib/kiosk-settings so the fleet
// heartbeat and the runtime can't drift.
const PERMISSION_REQUEST_MODE = readKioskPermissionRequestMode();

export interface GetStreamOptions {
  /** When true (QR mode), prefer back-facing camera for document scanning */
  preferBackCamera?: boolean;
}

interface CameraStreamManager {
  getStream(
    facingMode?: "user" | "environment",
    options?: GetStreamOptions,
  ): Promise<MediaStream>;
  releaseStream(): void;
  hasActiveStream(): boolean;
  isPermissionGranted(): boolean;
}

class CameraStreamManagerImpl implements CameraStreamManager {
  private stream: MediaStream | null = null;
  private activeUsers = 0;
  private initPromise: Promise<MediaStream> | null = null;
  private permissionGranted = false;
  private currentFacingMode: "user" | "environment" | null = null;

  private lastPreferBackCamera: boolean | undefined = undefined;

  async getStream(
    facingMode: "user" | "environment" = "user",
    options?: GetStreamOptions,
  ): Promise<MediaStream> {
    const preferBackCamera = options?.preferBackCamera;

    // If facing mode changed, release current stream to acquire new one
    if (
      this.stream?.active &&
      this.currentFacingMode !== null &&
      this.currentFacingMode !== facingMode
    ) {
      if (DEBUG_CAMERA_PROVIDER) {
        console.log(
          `[KioskCameraProvider] Switching camera from ${this.currentFacingMode} to ${facingMode}`,
        );
      }
      for (const track of this.stream.getTracks()) {
        track.stop();
      }
      this.stream = null;
      this.activeUsers = 0;
      this.initPromise = null;
    }

    // If we already have an active stream, return it
    if (this.stream?.active) {
      const optionsMatch = this.lastPreferBackCamera === preferBackCamera;
      if (optionsMatch) {
        for (const track of this.stream.getTracks()) {
          if (!track.enabled) track.enabled = true;
        }
        this.activeUsers++;
        return this.stream;
      }
      // Options changed (e.g. switched to QR mode) - stop and recreate
      for (const track of this.stream.getTracks()) {
        track.stop();
      }
      this.stream = null;
      this.initPromise = null;
    }

    // If we're already initializing, wait for that
    if (this.initPromise) {
      const stream = await this.initPromise;
      return stream;
    }

    // Initialize new stream
    // For facingMode-based selection, find the best camera
    const selectedDeviceId = await findPreferredCamera(facingMode);

    const videoConstraint = selectedDeviceId
      ? {
          deviceId: { exact: selectedDeviceId },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        }
      : {
          facingMode: facingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        };

    const acquire = () =>
      navigator.mediaDevices.getUserMedia({ video: videoConstraint });
    const delay = (ms: number) =>
      new Promise<void>((res) => setTimeout(res, ms));
    const isRetryable = (err: unknown) =>
      err instanceof DOMException &&
      (err.name === "NotReadableError" || err.name === "AbortError");

    // Retry with backoff only for transient hardware errors (camera busy).
    // Non-retryable errors (permission denied, device not found) fail fast.
    this.initPromise = acquire()
      .catch((e) =>
        isRetryable(e) ? delay(300).then(acquire) : Promise.reject(e),
      )
      .catch((e) =>
        isRetryable(e) ? delay(600).then(acquire) : Promise.reject(e),
      );

    try {
      this.stream = await this.initPromise;
      this.lastPreferBackCamera = preferBackCamera;
      this.permissionGranted = true;
      this.activeUsers = 1;
      this.currentFacingMode = facingMode;
      this.initPromise = null;
      return this.stream;
    } catch (error) {
      this.initPromise = null;
      console.error(
        "[KioskCameraProvider] Failed to acquire camera stream:",
        error,
      );
      throw error;
    }
  }

  releaseStream(): void {
    this.activeUsers = Math.max(0, this.activeUsers - 1);

    // Keep stream alive when releasing — stopping and reacquiring the camera
    // hardware fails on some Android devices. The stream will be reused by the
    // next getStream() call. Only stopStream() (provider unmount) fully releases.
    if (this.activeUsers === 0 && this.stream) {
      for (const track of this.stream.getTracks()) {
        track.enabled = false; // Mute but keep hardware allocated
      }
    }
  }

  // Only stop tracks when we're completely done (provider unmounts)
  stopStream(): void {
    if (this.stream) {
      for (const track of this.stream.getTracks()) {
        track.stop(); // This releases the camera
      }
      this.stream = null;
      this.activeUsers = 0;
      this.permissionGranted = false;
    }
  }

  hasActiveStream(): boolean {
    return !!this.stream?.active;
  }

  isPermissionGranted(): boolean {
    return this.permissionGranted;
  }

  // Request permission upfront by getting a stream and immediately stopping it
  async requestPermissionUpfront(): Promise<boolean> {
    // Check if we already have permission
    try {
      if (navigator.permissions) {
        const permissionStatus = await navigator.permissions.query({
          name: "camera" as PermissionName,
        });
        if (permissionStatus.state === "granted") {
          this.permissionGranted = true;
          // Don't get a stream yet - just mark permission as granted
          // Stream will be created when first needed
          return true;
        }
      }
    } catch {
      // Permissions API not supported, continue to request
    }

    // Request permission by getting a stream, then immediately stop it
    try {
      // Check for selected camera device in localStorage
      let selectedDeviceId =
        typeof window !== "undefined"
          ? localStorage.getItem("kiosk-selected-camera-device")
          : null;

      // Validate stored device ID is still valid, otherwise find a preferred camera
      if (selectedDeviceId) {
        const isValid = await isDeviceIdValid(selectedDeviceId);
        if (!isValid) {
          if (DEBUG_CAMERA_PROVIDER) {
            console.log(
              "[KioskCameraProvider] Stored device ID invalid during permission request, finding preferred camera",
            );
          }
          selectedDeviceId = await findPreferredCamera();
          if (selectedDeviceId) {
            localStorage.setItem(
              "kiosk-selected-camera-device",
              selectedDeviceId,
            );
          }
        }
      } else {
        // No stored device, find preferred camera
        selectedDeviceId = await findPreferredCamera();
        if (selectedDeviceId) {
          localStorage.setItem(
            "kiosk-selected-camera-device",
            selectedDeviceId,
          );
        }
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: selectedDeviceId
          ? {
              deviceId: { exact: selectedDeviceId },
              width: { ideal: 1920 },
              height: { ideal: 1080 },
            }
          : {
              // Fallback to front camera if no device found
              facingMode: "user",
              width: { ideal: 1920 },
              height: { ideal: 1080 },
            },
      });

      // Immediately stop tracks (turns off camera indicator)
      // Permission is now granted and will persist
      for (const track of stream.getTracks()) {
        track.stop();
      }

      // Don't keep the stream - permission is what matters
      this.stream = null;
      this.permissionGranted = true;
      return true;
    } catch (error) {
      console.error(
        "[KioskCameraProvider] ❌ Failed to get camera permission upfront:",
        error,
      );
      this.permissionGranted = false;
      return false;
    }
  }
}

interface KioskCameraContextValue {
  getStream: (
    facingMode?: "user" | "environment",
    options?: GetStreamOptions,
  ) => Promise<MediaStream>;
  releaseStream: () => void;
  hasActiveStream: () => boolean;
  isPermissionGranted: () => boolean;
  isRequestingPermission: boolean;
}

const KioskCameraContext = createContext<KioskCameraContextValue | undefined>(
  undefined,
);

interface KioskCameraProviderProps {
  children: React.ReactNode;
}

export function KioskCameraProvider({ children }: KioskCameraProviderProps) {
  const managerRef = useRef<CameraStreamManagerImpl | null>(null);
  const [isRequestingPermission, setIsRequestingPermission] = useState(true);

  // Initialize manager once
  if (!managerRef.current) {
    managerRef.current = new CameraStreamManagerImpl();
  }

  // Request permission upfront on mount (if configured to do so)
  useEffect(() => {
    let mounted = true;

    const requestPermission = async () => {
      setIsRequestingPermission(true);
      try {
        if (managerRef.current) {
          await managerRef.current.requestPermissionUpfront();
        }
      } catch (error) {
        console.error(
          "[KioskCameraProvider] Error requesting permission:",
          error,
        );
      } finally {
        if (mounted) {
          setIsRequestingPermission(false);
        }
      }
    };

    // Only request if configured for STARTUP mode
    if (PERMISSION_REQUEST_MODE === "startup") {
      // Only request if we're in a browser environment
      if (typeof window !== "undefined" && navigator.mediaDevices) {
        requestPermission();
      } else {
        setIsRequestingPermission(false);
      }
    } else {
      // ON_DEMAND mode - skip upfront request
      setIsRequestingPermission(false);
    }

    return () => {
      mounted = false;
    };
  }, []);

  const getStream = useCallback(
    async (facingMode?: "user" | "environment", options?: GetStreamOptions) => {
      if (!managerRef.current) {
        throw new Error("Camera manager not initialized");
      }
      return managerRef.current.getStream(facingMode, options);
    },
    [],
  );

  const releaseStream = useCallback(() => {
    if (managerRef.current) {
      managerRef.current.releaseStream();
    }
  }, []);

  const hasActiveStream = useCallback(() => {
    return managerRef.current?.hasActiveStream() ?? false;
  }, []);

  const isPermissionGranted = useCallback(() => {
    return managerRef.current?.isPermissionGranted() ?? false;
  }, []);

  // Listen for camera device changes from DevTool
  useEffect(() => {
    const handleCameraChange = (e: Event) => {
      const customEvent = e as CustomEvent<{
        selectedCameraDeviceId?: string;
      }>;
      if (customEvent.detail.selectedCameraDeviceId) {
        // Stop current stream to force recreation with new device
        if (managerRef.current) {
          managerRef.current.stopStream();
        }
      }
    };

    window.addEventListener("kiosk-video-settings-changed", handleCameraChange);
    return () => {
      window.removeEventListener(
        "kiosk-video-settings-changed",
        handleCameraChange,
      );
    };
  }, []);

  // Cleanup on provider unmount - stop stream completely
  useEffect(() => {
    return () => {
      if (managerRef.current) {
        managerRef.current.stopStream();
      }
    };
  }, []);

  const value: KioskCameraContextValue = {
    getStream,
    releaseStream,
    hasActiveStream,
    isPermissionGranted,
    isRequestingPermission,
  };

  return (
    <KioskCameraContext.Provider value={value}>
      {children}
    </KioskCameraContext.Provider>
  );
}

export function useKioskCamera() {
  const context = useContext(KioskCameraContext);
  if (!context) {
    throw new Error("useKioskCamera must be used within KioskCameraProvider");
  }
  return context;
}

export function useKioskCameraSafe() {
  return useContext(KioskCameraContext) ?? null;
}
